import { scaleLinear } from "d3-scale";
import { t } from "i18next";
import dayjs, { ManipulateType } from "dayjs";
import { Dayjs } from "../lib";
import { SelectOption as SelectOptionDS } from "@shape-ds/components";
import { useMemo } from "react";

const MONTHS_TO_SHOW = 12;

export const getCssVar = (variable: string) => {
  return window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(variable);
};

export const scaleCalculator = (
  values: number[],
  offset = 0,
  range = [0, 1]
) => {
  let min = 0;
  let max = 0.3;
  if (values.length) {
    [min, max] = [
      Math.floor(Math.min(...values)),
      Math.ceil(Math.max(...values)),
    ];
  }

  const [startValue, endValue] = [
    min === 0 ? min : min - Math.abs((min / 100) * offset),
    max + Math.abs((max / 100) * offset),
  ];

  return {
    getValueScale: scaleLinear().domain([startValue, endValue]).range(range),
    getScaledValue: scaleLinear().domain(range).range([startValue, endValue]),
  };
};

export const getDatetimeFormatByTimeDiff = (
  timeDiff: number,
  unit: "days" | "hours" | "minutes" | "seconds" = "hours"
) => {
  let diffInMinutes;
  if (unit === "seconds") {
    diffInMinutes = timeDiff / 60;
  } else if (unit === "hours") {
    diffInMinutes = timeDiff * 60;
  } else if (unit === "days") {
    diffInMinutes = timeDiff * 60 * 24;
  } else {
    diffInMinutes = timeDiff;
  }

  switch (true) {
    case diffInMinutes < 10:
      return "HH:mm:ss";
    case diffInMinutes < 60 * 24 * 2:
      return "HH:mm";
    default:
      return t("formats:date.lts");
  }
};

export const filterUniqueItems = (
  items: string[],
  selectedOption: string
): string[] => {
  const updatedItens = new Set(items);
  if (updatedItens.has(selectedOption)) {
    updatedItens.delete(selectedOption);
  } else {
    updatedItens.add(selectedOption);
  }
  return [...updatedItens];
};

export const formatNumberWithDecimalPlaces = (
  number: number,
  decimalPlaces = 1,
  ignoreInteger = true
): string =>
  number.toFixed(Number.isInteger(number) && ignoreInteger ? 0 : decimalPlaces);

export const colorsPalette = [
  "#226CFF",
  "#00B21D",
  "#E6CF00",
  "#D400E7",
  "#FF8A00",
  "#00C9E4",
  "#96CD00",
  "#6774FF",
  "#FF6565",
  "#65CB76",
  "#AA58B1",
  "#007E14",
  "#B0B0B0",
  "#666666",
  "#CD3A3A",
  "#807300",
  "#8E009A",
  "#C56B00",
  "#00889A",
  "#668000",
  "#5961B1",
  "#B4A200",
  "#FFAD4D",
  "#59A8B2",
  "#ABBE60",
  "#B15959",
  "#4E9A5A",
  "#CBBF51",
  "#C479CB",
  "#CD9452",
  "#7AC2CC",
  "#8C985B",
  "#3A8700",
  "#4CA9FF",
  "#FF008A",
  "#A64DFF",
  "#0077E5",
  "#B4367A",
  "#7435B2",
];

export const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  waitFor: number
) => {
  let timeout: NodeJS.Timeout;

  const debounced = (...args: Parameters<F>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced;
};

export enum EDateFormats {
  API_DATE_FORMAT = "YYYY-MM-DD",
  MMMYY = "MMM-YY",
  MONTH_YEAR_FORMAT = "MM-YYYY",
  DDMMM = "DD-MMM",
  DDMMMYY = "DD-MMM-YY",
  MMYY = "MM/YY",
  DDMMYYYY = "DD/MM/YYYY",
  DDMMYY = "DD/MM/YY",
  DDMMMYYYY = "DD-MMM-YYYY",
  YYYY_MM = "YYYY-MM",
  TITLE_FORMAT = "MMMM YYYY",
  FULL_TEXT_FORMAT = "DD MMMM YYYY",
  YYYYMMDD_HHMMSS = "YYYY-MM-DD HH:mm:ss",
  DDMMYYYY_HHMMSS = "DD/MM/YYYY HH:mm:ss",
  DDMMYY_HHMM = "DD/MM/YY HH:mm",
  DDMM_HH = "DD/MM HH",
  ABBREVIATED_MONTH = "MMM",
  DATE_SNIPPET = "dddd, DD MMM YYYY",
  MMMDDYYYY = "MMM dd, yyyy",
  DEFAULT_DATE_FORMAT = "dd-MM-yyyy",
}

export function removeUnderline<T extends Record<string, string>>(
  text: string | T[keyof T]
): string {
  return typeof text === "string" ? text.replace(/_/g, " ") : text;
}

export const changePeriod = (
  alarmDateRange: { start: Dayjs | string; end: Dayjs | string },
  period: string
) => {
  const alarmMapPeriod: {
    [key: number]: { unit: ManipulateType; value: number };
  } = {
    NaN: { unit: "hour", value: 1 },
    6: { unit: "hour", value: 3 },
    24: { unit: "hour", value: 12 },
    168: { unit: "day", value: 3.5 },
    720: { unit: "day", value: 15 },
    1440: { unit: "day", value: 30 },
    2160: { unit: "day", value: 45 },
    4320: { unit: "day", value: 90 },
    8760: { unit: "day", value: 180 },
    17520: { unit: "day", value: 365 },
    35040: { unit: "day", value: 730 },
  };

  const today = dayjs();
  const alarm_dt_start = dayjs(alarmDateRange.start).utc();
  let alarm_dt_end = dayjs(alarmDateRange.end).utc();

  let start = 1;
  let end = 1;
  const hours = parseInt(period, 10);

  const { value, unit } = alarmMapPeriod[hours];

  if (period !== "alarm") {
    const end_diff = today.diff(alarm_dt_end, unit);

    if (value >= end_diff) {
      start = value;
      end = 0;
      alarm_dt_end = today;
    } else {
      start = value;
      end = value;
    }
  }

  if (value === 3.5) {
    return {
      start: alarm_dt_start.subtract(start, unit).subtract(12, "hour"),
      end: alarm_dt_end.add(end, unit).add(12, "hour"),
    };
  }

  if (value === 1) {
    const duration = alarm_dt_end.diff(alarm_dt_start, "hour");

    return {
      start: alarm_dt_start.subtract(duration * 2.33, "hour"),
      end: alarm_dt_end.add(3, "hour"),
    };
  }

  return {
    start: alarm_dt_start.subtract(start, unit),
    end: alarm_dt_end.add(end, unit),
  };
};

export function getAllMonths(format = "MMM/YY"): SelectOptionDS[] {
  const baseDatesArray = new Array(MONTHS_TO_SHOW).fill("");
  return baseDatesArray.map((_, index) => {
    const monthDate = dayjs().subtract(index, "M");
    const formattedMonth = monthDate.format(format);

    return { label: formattedMonth, value: formattedMonth };
  });
}

export const vesselsCodeList: { [key: string]: string } = {
  MV15: "0149",
  MVFLU: "0152",
  MV18: "0201",
  MV20: "0212",
  MV22: "0262",
  MV23: "0280",
  MV24: "0355",
  MV26: "0381",
  MV27: "0397",
  MV29: "0428",
  MV30: "0478",
  MV31: "0468",
  MV32: "0479",
  MV33: "4006",
};

export const useDatesHelper = () => {
  const today = useMemo(() => dayjs(), []);
  return {
    buildDateRange: (period: string | number, initDate?: Dayjs) => ({
      startDate: (initDate ?? today)
        .subtract(+period, "days")
        .format("YYYY-MM-DD"),
      endDate: (initDate ?? today).format("YYYY-MM-DD"),
    }),
  };
};
export const toCapitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};
